import React, { useMemo } from 'react';
import { NewAnomalyItem, Column } from '../../../../utils/models';
import { getAnomalyClassStyle, getAnomalyTypeStyle, getChunkedData, getIsAnomalyWithCMD } from '../../../../utils/utilities';

import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';
import useProjectDetails from '../../../../hooks/useProjectDetails';
import { getReportDetails } from '../../../../store/reportDetails';
import { useAppSelector } from '../../../../store/configureStore';

interface Props {
    anomalyItems: NewAnomalyItem[];
    sectionTitle?: string;
    sectionSubtitle?: string;
}

const CustomVisualInspectionSummary: React.FC<Props> = ({ anomalyItems, sectionSubtitle, sectionTitle = 'Visual Inspection Summary' }) => {
    const { projectDetails } = useProjectDetails();
    const { itemCountPerPage } = useAppSelector(getReportDetails);

    const hasCMD = useMemo(() => {
        return anomalyItems.some((anomaly) => anomaly.cmd);
    }, [anomalyItems]);

    const columns = useMemo(() => {
        const columns: Column[] = [
            {
                path: 'id',
                label: 'Sr No.',
            },
            {
                path: 'description',
                label: 'Anomaly Description',
                content: (item: NewAnomalyItem) => <span>{item.description.map(({value}) => value).join('. ')}</span>,
                style: (item: NewAnomalyItem) => ({ textAlign: 'left' }),
            },
            {
                path: 'location',
                label: 'Anomaly Location',
                style: (item: NewAnomalyItem) => ({ textAlign: 'left' }),
            },
            {
                path: 'class',
                label: 'Anomaly Class',
                content: (item: NewAnomalyItem) => <span>{item.class}</span>,
                style: (item: NewAnomalyItem) => isNaN(item.class) ? getAnomalyTypeStyle(item) : getAnomalyClassStyle(item),
            },
        ];

        if (projectDetails && getIsAnomalyWithCMD(projectDetails) && hasCMD) {
            const cmsPath = {
                path: 'cmd',
                label: 'CDR',
            };
            
            const [first, ...others] = columns;

            return [first, cmsPath, ...others];
        }

        return columns;
    }, [projectDetails, hasCMD]);

    return ( 
            <>
                <Conditional isVisible={anomalyItems.length > 0}>
                    {getChunkedData(anomalyItems, itemCountPerPage).map((items, index) => (
                        <CustomSection key={index} sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle}>
                            <div className="p-1 table-responsive">
                                <PDFTablePage data={items} columns={columns} />
                            </div>
                        </CustomSection>
                    ))}
                </Conditional>

               <Conditional isVisible={anomalyItems.length === 0}>
                    <CustomSection sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle}>
                        <div className="p-1">
                            <PDFTablePage data={anomalyItems} columns={columns} />
                        </div>
                    </CustomSection>
               </Conditional>
            </>
     );
};

export default CustomVisualInspectionSummary;